import { useGLTF } from '@react-three/drei';
import { Home, FileText, ChevronLeft, ChevronRight, ChevronDown, X, Check } from 'react-feather';

export interface InfoPoint {
    name: string;
    description: string;
    position: [number, number, number];
    lookAt: [number, number, number];
    Object: (key: number) => JSX.Element;
}

export interface Plant {
    scientificName: string;
    commonName: string;
    img: string;
    infoPoints: InfoPoint[];
}

export const plants: Plant[] = [
    {
        scientificName: 'camassia quamash',
        commonName: 'Common Camas',
        img: './assets/plants/camassia-quamash.png',
        infoPoints: [
            {
                name: 'Overview',
                description: 'COMMON CAMAS is a perennial herb native to the west coast of North America. It belongs to the lily family. Common Camas sprouts from a white bulb at its root, which is edible. A similar looking-species, Death-camas (also known as white camas), distinguished by its white flowers, has bulb that are highly toxic and potentially fatal. Camas (also known as quamash) were given its name by the local Nimiipuu (Nez Perce) tribe, who are native to present-day Oregon, Idaho, and Washington. The city of Camas, Washington was named after the extensive local trade of the Camas bulb.',
                position: [-1.0, 1.0, 1.0],
                lookAt: [-0.75, 0.5, -0.5],
                Object: () => {
                    const plantPot = useGLTF("./assets/models/plant-pot.glb");
                    const plant = useGLTF("./assets/models/plants/common-camas.glb");
                    return (
                        <>
                            <primitive object={plantPot.scene.clone()} position={[-1.25, 0.05, -0.5]} scale={0.3} rotation={[0, 0, 0]} />
                            <primitive object={plant.scene.clone()} position={[-1.25, 0.3, -0.5]} scale={0.4} rotation={[0, 0, 0]} />
                        </>
                    );
                },
            },
            {
                name: 'Description',
                description: 'Common Camas are around one to three feet tall. Common Camas have beautiful light purple flowers, and can often be found in patches. The edible bulbs at the root of Common Camas are white and around two inches in diameter. Common Camas also look very similar in appearance to Great Camas, which flowers instead twist together to protect the fruit.',
                position: [-0.7, 0.3, 0.9],
                lookAt: [-0.8, 0.15, 0.15],
                Object: () => {
                    const pictureFrame0 = useGLTF("./assets/models/picture-frame-1-0.glb");
                    const pictureFrame1 = useGLTF("./assets/models/picture-frame-1-1.glb");
                    return (
                        <>
                            <primitive object={pictureFrame0.scene.clone()} position={[-1.2, 0.05, 0.2]} scale={0.1} rotation={[0, -Math.PI / 2 + 0.5, 0]} />
                            <primitive object={pictureFrame1.scene.clone()} position={[-0.9, 0.05, 0.1]} scale={0.1} rotation={[0, -Math.PI / 2, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Region',
                description: 'Common Camas can be found on the west coast, prominently in the pacific northwest of the United States and western parts of Canada. Common Camas grows in wet grassy meadows and prairies, particularly in rainshadow (the side of a mountain that receives less precipitation) areas at low to mid elevations.',
                position: [-0.3, 1, 0.5],
                lookAt: [-0.3, 0.06, 0.49],
                Object: () => {
                    const regionMap = useGLTF("./assets/models/region-map-1.glb");
                    return (
                        <>
                            <primitive object={regionMap.scene.clone()} position={[-0.5, 0.06, 0.5]} scale={0.25} rotation={[0, -Math.PI / 2 + 0.1, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Cultivation',
                description: 'Camas patches are routinely harvested for their bulbs in the summer, which traditionally took the many indigenous tribes weeks to months. The bulbs are harvested after flowering, so as to not confuse with the white-flowered Death-camas. Bulbs were dug with pointed digging sticks, and the larger plants were harvested first, with the smaller left to grow for longer. Controlled burning was sometimes used to promote growth. Common Camas are discouraged to harvest in the wild. It is propagated using the seeds or bulbs of the plant. Common camas requires a decent amount of moisture or irrigation to grow.',
                position: [0.0, 1.0, 0.0],
                lookAt: [0.1, 0.3, -0.6],
                Object: () => {
                    const planterBox = useGLTF("./assets/models/planter-box-1.glb");
                    return (
                        <>
                            <primitive object={planterBox.scene.clone()} position={[0.1, 0.05, -0.6]} scale={0.4} rotation={[0, -Math.PI / 2 - 0.1, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Uses: Edible',
                description: 'The primary use of Common Camas is its edible bulb. The bulbs are considered a delicacy by the local Blackfoot, Cree, and Nez Perce. Bulbs can be cooked or steamed in fire pits for up to 24 hours, which breaks down the complex carbohydrates into fructose, giving the cooked bulbs a sweet flavor. At times it was used as a sweetener, which was a rare property before European colonization. Occasionally, the cooked bulbs would be mashed and turned into cakes or bricks, for taste or long-term storage.',
                position: [0.9, 2.0, -0.3],
                lookAt: [1.2, 0.05, -0.1],
                Object: () => {
                    const spread = useGLTF("./assets/models/camas-edible-spread.glb");
                    return (
                        <>
                            <primitive object={spread.scene.clone()} position={[1.2, 0.05, -0.3]} scale={0.4} rotation={[0, - 0.1, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Uses: Trade',
                description: 'Common camas was one of the most traded food items by local natives, especially the Nez Perce. It has significant value, with many traveling far to harvest common Camas for themselves. Other valuable uses of the Camas Plant include using the stalks and leaves for mattresses and is used by the Nez Perce as a cough medicine when mixed with honey. The purple flowers are also used ornamentally.',
                position: [2.0, 1.0, 0.5],
                lookAt: [1.0, 0.05, 0.3],
                Object: () => {
                    const bowl = useGLTF("./assets/models/camas-bowl.glb");
                    return (
                        <>
                            <primitive object={bowl.scene.clone()} position={[1.0, 0.05, 0.5]} scale={0.4} rotation={[0, - 0.1, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Damage',
                description: 'Wild camas patches have been significantly damaged by urbanization and agriculture, although there are still persistent camas patches in the wild.',
                position: [0.6, 0.5, 1.0],
                lookAt: [0.5, 0.05, 0.5],
                Object: () => {
                    const dead = useGLTF("./assets/models/dead-camas.glb");
                    return (
                        <>
                            <primitive object={dead.scene.clone()} position={[0.2, 0.07, 0.5]} scale={0.7} rotation={[0, - 0.1, 0]} />
                        </>
                    )
                }
            }
        ],
    },
    {
        scientificName: 'gaultheria shallon',
        commonName: 'Salal',
        img: './assets/plants/gaultheria-shallon.png',
        infoPoints: [
            {
                name: 'Overview',
                description: 'SALAL is a very common evergreen understory shrub found primarily on the west coast of North America. You have most likely seen it on a trip to the Oregon coast. The juicy purple berries are plentiful and were an important food source for many indigenous tribes, as well as being a popular trade item. Additionally, different parts of the Salal plant have medicinal properties. Salal’s name comes from the Chinook word “sallal,” and is sometimes referred to as “lemon-leaf”.',
                position: [-1.0, 1.0, 1.0],
                lookAt: [-0.75, 0.5, -0.5],
                Object: () => {
                    const plantPot = useGLTF("./assets/models/plant-pot.glb");
                    const plant = useGLTF("./assets/models/plants/salal.glb");
                    return (
                        <>
                            <primitive object={plantPot.scene.clone()} position={[-1.25, 0.05, -0.5]} scale={0.3} rotation={[0, 0, 0]} />
                            <primitive object={plant.scene.clone()} position={[-1.25, 0.3, -0.5]} scale={0.15} rotation={[0, 0, 0]} />
                        </>
                    );
                },
            },
            {
                name: 'Description',
                description: 'Salal can grow between 0.6 and 12 feet tall, forming large hedges. Its evergreen leaves are a shiny green, but newer leaves can be bright red. Flowers and fruits hang from red stalks that protrude from the stem. Salal is classified as an invasive species (but is native to North America). Salal leaves are a popular choice for browsing deer.',
                position: [-0.6, 0.3, 0.9],
                lookAt: [-0.7, 0.15, 0.15],
                Object: () => {
                    const pictureFrame0 = useGLTF("./assets/models/picture-frame-2-0.glb");
                    const pictureFrame1 = useGLTF("./assets/models/picture-frame-2-1.glb");
                    return (
                        <>
                            <primitive object={pictureFrame0.scene.clone()} position={[-1.1, 0.05, 0.2]} scale={0.1} rotation={[0, -Math.PI / 2 + 0.4, 0]} />
                            <primitive object={pictureFrame1.scene.clone()} position={[-0.8, 0.05, 0.1]} scale={0.1} rotation={[0, -Math.PI / 2 - 0.2, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Region',
                description: 'Salal is found in coastal coniferous forest regions, but can also be found in forested wetlands throughout the pacific northwest. It is found in the western United States, in the states of Washington, Oregon, and California, but grows as far north as Canada to Alaska.',
                position: [-0.1, 1, -0.6],
                lookAt: [-0.1, 0.06, -0.61],
                Object: () => {
                    const regionMap = useGLTF("./assets/models/region-map-2.glb");
                    return (
                        <>
                            <primitive object={regionMap.scene.clone()} position={[-0.4, 0.06, -0.6]} scale={0.25} rotation={[0, -Math.PI / 2 + 0.1, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Cultivation',
                description: 'I found no evidence of Salal being intentionally cultivated by Native Americans, presumably due to how common it is in the wild and how easy it is to harvest. Salal remains a popular plant for home gardens, and it grows best in moist climates. You can find Salal seeds in your local garden store.',
                position: [0.6, 1.0, 0.6],
                lookAt: [0.5, 0.3, 0.3],
                Object: () => {
                    const salalSeeds = useGLTF("./assets/models/salal-seeds.glb");
                    return (
                        <>
                            <primitive object={salalSeeds.scene.clone()} position={[0.0, 0.05, 0.2]} scale={0.06} rotation={[0, -Math.PI / 2 - 0.1, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Uses: Edible',
                description: "Salal berries were a popular and important fruit for indigenous tribes that lived along the coast. The Kwakwaka'wakw people consumed Salal berries dipped in oolichan (Eulachon) grease at feasts. Salal berries were also used as sweeteners due to their mildly sweet flavor. Many would mash and boil the berries into cakes, and even today, salal berries are commonly made into jams and jellies.",
                position: [1.4, 0.2, 1.0],
                lookAt: [1.1, 0.05, -0.3],
                Object: () => {
                    const spread = useGLTF("./assets/models/salal-edible-spread.glb");
                    return (
                        <>
                            <primitive object={spread.scene.clone()} position={[0.8, 0.05, -0.3]} scale={0.15} rotation={[0, - 1.2, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Uses: Medicinal',
                description: 'Salal leaves were chewed to suppress hunger, and they actually have anti-inflammatory properties. Salal leaves have also been known to treat gastrointestinal problems, and sometimes were made in a poultice to treat wounds. Salal berries are also rich in antioxidants.',
                position: [1.5, 1.0, 0.41],
                lookAt: [1.5, 0.05, 0.4],
                Object: () => {
                    const salalLeaves = useGLTF("./assets/models/salal-leaves.glb");
                    return (
                        <>
                            <primitive object={salalLeaves.scene.clone()} position={[1.4, 0.05, 0.4]} scale={0.08} rotation={[0, -Math.PI / 2 - 0.1, 0]} />
                        </>
                    )
                }
            }
        ],
    },
    {
        scientificName: 'thuja plicata',
        commonName: 'Western Red Cedar',
        img: './assets/plants/thuja-plicata.png',
        infoPoints: [
            {
                name: 'Overview',
                description: 'WESTERN RED CEDAR is a large coniferous evergreen tree, native to the west coast of the United States. It is perhaps best known by its splitting bark, which you can pull right off the tree. Western Red Cedar bark is one of the best materials for firestarting. The tree is also considered to be one of the most important trees for indigenous groups in the pacific northwest. The wood and bark are versatile, used for baskets, clothing, and even canoes. It is also the provincial tree of British Columbia. Some local groups, including the Kwakwaka’wakw, call it the “tree of life”.',
                position: [-1.0, 1.0, 1.0],
                lookAt: [-0.75, 0.5, -0.5],
                Object: () => {
                    const plantPot = useGLTF("./assets/models/plant-pot.glb");
                    const plant = useGLTF("./assets/models/plants/western-red-cedar.glb");
                    return (
                        <>
                            <primitive object={plantPot.scene.clone()} position={[-1.25, 0.05, -0.5]} scale={0.3} rotation={[0, 0, 0]} />
                            <primitive object={plant.scene.clone()} position={[-1.25, 0.3, -0.5]} scale={0.4} rotation={[0, 0, 0]} />
                        </>
                    );
                },
            },
            {
                name: 'Description',
                description: 'Western Red Cedars can grow up to 200 feet tall. The glossy leaves, looking like needles at first, look braided, and have very small pollen cones. The bark of the tree is in vertical strips, which are relatively easy to harvest by hand.',
                position: [-0.6, 0.3, 0.9],
                lookAt: [-0.7, 0.15, 0.15],
                Object: () => {
                    const pictureFrame0 = useGLTF("./assets/models/picture-frame-3-0.glb");
                    const pictureFrame1 = useGLTF("./assets/models/picture-frame-3-1.glb");
                    return (
                        <>
                            <primitive object={pictureFrame0.scene.clone()} position={[-1.1, 0.05, 0.2]} scale={0.1} rotation={[0, -Math.PI / 2 + 0.4, 0]} />
                            <primitive object={pictureFrame1.scene.clone()} position={[-0.8, 0.05, 0.1]} scale={0.1} rotation={[0, -Math.PI / 2 - 0.2, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Region',
                description: 'Western Red Cedars are found along the west coast, in Washington, Oregon, and California, although they are a little more selective about their environment. They mostly grow west of the Cascades, in wet climates, but some trees grow as far north as Alaska.',
                position: [-0.1, 1, -0.5],
                lookAt: [-0.1, 0.06, -0.51],
                Object: () => {
                    const regionMap = useGLTF("./assets/models/region-map-3.glb");
                    return (
                        <>
                            <primitive object={regionMap.scene.clone()} position={[-0.4, 0.06, -0.5]} scale={0.25} rotation={[0, -Math.PI / 2 - 0.1, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Cultivation',
                description: 'I also found no evidence of Cultivation by Native Americans, presumably because these trees are relatively common and naturally regrow. In the present day, Western Red Cedar are cultivated for ornamental purposes.',
                position: [0.2, 1.0, 0.21],
                lookAt: [0.2, 0.05, 0.2],
                Object: () => {
                    const cedarCultivation = useGLTF("./assets/models/cedar-cultivation.glb");
                    return (
                        <>
                            <primitive object={cedarCultivation.scene.clone()} position={[-0.2, 0.05, 0.2]} scale={0.04} rotation={[0, -Math.PI / 2 + 0.4, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Uses: Craft',
                description: 'The primary use of Western Red Cedars was in the manufacture of useful items. According to Plants of The Pacific Northwest Coast, these included: “important cultural items such as dugout canoes, house planks and posts, totem and mortuary poles, bentwood boxes, baskets, clothing and hats, and a variety of tools and implements such as dishes, arrow shafts, harpoon shafts, spear poles, barbecue sticks, fish spreaders and hangers, dip-net hooks, fish clubs, masks, rattles, benches, cradles, coffins, herring rakes, canoe bailers, ceremonial drum logs, combs, fishing floats, berry-drying racks, fish weirs, spirit whistles, and paddles.” Red Cedar wood is also rot-resistant, which makes it a natural choice for building shelters.',
                position: [2.0, 0.2, 1.0],
                lookAt: [1.6, 0.05, -0.1],
                Object: () => {
                    const spread = useGLTF("./assets/models/cedar-buildings.glb");
                    return (
                        <>
                            <primitive object={spread.scene.clone()} position={[1.0, 0.05, -0.3]} scale={0.25} rotation={[0, -0.9, 0]} />
                        </>
                    )
                }
            },
            {
                name: 'Uses: Fire Starting',
                description: 'Western Red Cedar bark, when stripped and split into strands, makes excellent tinder, and the wood of the tree also works as excellent fuel. Western Red Cedar does not produce much smoke when burnt, which makes it optimal for drying fish. Western Red Cedar was also used by the Kwakwaka’wakw to construct bow drills for making friction fires.',
                position: [0.2, 1.0, 1.2],
                lookAt: [0.7, 0.05, 0.7],
                Object: () => {
                    const cedarFire = useGLTF("./assets/models/cedar-campfire.glb");
                    return (
                        <>
                            <primitive object={cedarFire.scene.clone()} position={[0.5, 0.05, 0.5]} scale={0.15} rotation={[0, -Math.PI / 2 - 0.1, 0]} />
                        </>
                    )
                }
            }
        ],
    },
];

function mod(n: number, m: number) {
    return ((n % m) + m) % m;
}

export default function Display({ plant, back, bibLink, index, setIndex }: { plant: Plant, back?: () => void, bibLink?: () => void, index: number, setIndex: (index: number) => void })  {

    const length = plant.infoPoints.length + 1;
    const infoIndex = index - 1;
    return (
        <div className='relative w-screen h-screen min-w-full min-h-screen'>
            <div className='absolute right-0 top-0 h-screen w-1/2 flex items-center flex-row'>
                {index === 0 ? (<></>) : (
                    <div className='displayBox mr-32'>
                        <h2 className='text-left'>{plant.infoPoints[infoIndex].name}</h2>
                        <p className='text-justify my-8 leading-relaxed mx-0'>{plant.infoPoints[infoIndex].description}</p>
                        <div className='flex flex-row gap-1'>
                            <button onClick={() => setIndex(mod(index - 1, length))} className='p-4'>
                                {infoIndex === 0 ? <X /> : <ChevronLeft />}
                            </button>
                            <button onClick={() => setIndex(mod(index + 1, length))} className='p-4'>
                                {index === length - 1 ? <Check /> : <ChevronRight />}
                            </button>
                            {index === length - 1 && (
                                <>
                                <div className='flex-grow'></div>
                                <button onClick={back} className='p-4'>
                                    <Home />
                                </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className='absolute top-0 left-0 w-full text-center mt-8 flex flex-col items-center'>
                <h2 className='text-2xl'>{plant.commonName}</h2>
                <h3 className='text-lg italic'>{plant.scientificName}</h3>
                <div className='plant flex flex-row gap-2 rounded-full mt-4'>
                    <ChevronLeft className='p-0 cursor-pointer mr-1' onClick={() => setIndex(mod(index - 1, length))}/>
                    {new Array(length).fill(null).map((_, i) => (
                        <div className={`${i === index ? 'activeInfo' : ''} w-2 h-2 my-2 plant rounded-full transition-colors duration-500`} key={i} />
                    ))}
                    <ChevronRight className='p-0 cursor-pointer ml-1' onClick={() => setIndex(mod(index + 1, length))}/>
                </div>
            </div>
            <button className='absolute top-0 left-0 m-8 max-w-min rounded-full p-4' onClick={back}>
                <Home />
            </button>
            <button className='absolute top-0 right-0 m-8 max-w-min rounded-full p-4' onClick={bibLink}>
                <FileText />
            </button>
            <div className='absolute bottom-0 w-full left-0'>
                {index === 0 && (
                    <div className='flex flex-row justify-center items-center displayBox'>
                        <button className='p-4' onClick={() => setIndex(1)}>
                            <ChevronDown />
                        </button>
                    </div>   
                )}
            </div>
        </div>
    );
}